// ============================================================================
// FSA TRIAL BALANCE — ENTRY VIEW MODAL  (FSA-2.4 UI polish)
// ============================================================================
// Small read-only popup that shows the full per-ledger breakdown for a
// single TB row. Replaces the Consolidated + Journal Net columns that
// were dropped from the main grid — both values surface here alongside
// Opening, Audited, classification metadata, and the JE-introduced flag.
//
// Trigger: "View details" row action (eye icon) on FsaTrialBalanceTab.
// Read-only by design — edits flow through the existing Edit
// Classification modal (separate row action).

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { TrialBalanceEntry } from 'api/types/fsa/trialBalance.types';

interface Props {
  isOpen: boolean;
  entry: TrialBalanceEntry | null;
  /** Per-ledger audited row (consolidated + JE net) sourced from auditedBalances. */
  audited?: {
    journalNet: number;
    audited: number;
    isJeCreated: boolean;
  } | null;
  /** Engagement-time custom class name lookup (when entry.customClassLocalId is set). */
  customClassName?: string | null;
  onClose: () => void;
}

const fmtAmt = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateTime = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

const FsaTrialBalanceEntryViewModal: React.FC<Props> = ({
  isOpen, entry, audited, customClassName, onClose,
}) => {
  if (!isOpen || !entry) return null;

  const opening = entry.openingAmount ?? 0;
  const consolidated = entry.closingAmount ?? 0;
  const jeNet = audited?.journalNet ?? 0;
  const auditedAmt = audited?.audited ?? consolidated;

  const classifySource = !entry.scheduleClassId
    ? null
    : entry.isAutoClassified ? 'Auto (hint dictionary)' : 'Manual';

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        className="modal-container modal-md"
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}
      >
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconSystem.eye size={18} />
            <span>Ledger Details</span>
            {audited?.isJeCreated && (
              <span style={jeChipStyle} title="Created by a posted journal entry — not present in source TB">
                JE-introduced
              </span>
            )}
          </h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ flex: 1, overflow: 'auto' }}>
          {/* Identity */}
          <section style={sectionStyle}>
            <h3 style={sectionHeaderStyle}>Identity</h3>
            <dl style={defListStyle}>
              <dt style={dtStyle}>Ledger</dt>
              <dd style={ddStyle}><strong>{entry.ledgerName}</strong></dd>
              <dt style={dtStyle}>Group</dt>
              <dd style={ddStyle}>{entry.parentGroup || <span style={mutedStyle}>—</span>}</dd>
            </dl>
          </section>

          {/* Amounts breakdown */}
          <section style={sectionStyle}>
            <h3 style={sectionHeaderStyle}>Amount Breakdown</h3>
            <table style={amtTableStyle}>
              <tbody>
                <AmtRow label="Opening" value={opening} />
                <AmtRow label="Consolidated (pre-JE)" value={consolidated} />
                <AmtRow label="Journal Net (Σ posted JEs)" value={jeNet} muted={Math.abs(jeNet) < 0.005} />
                <AmtRow label="Audited (Consolidated + JE Net)" value={auditedAmt} emphasized />
              </tbody>
            </table>
            <p style={hintStyle}>
              Audited = Consolidated + Journal Net. Posted Adjustment
              Journals contribute via Journal Net; the Audited Closing TB
              export uses this composed value.
            </p>
          </section>

          {/* Classification */}
          <section style={sectionStyle}>
            <h3 style={sectionHeaderStyle}>Classification</h3>
            <dl style={defListStyle}>
              <dt style={dtStyle}>Schedule</dt>
              <dd style={ddStyle}>{entry.scheduleName || <span style={mutedStyle}>Unclassified</span>}</dd>
              <dt style={dtStyle}>Class</dt>
              <dd style={ddStyle}>
                {entry.scheduleClassName
                  ? entry.scheduleClassName
                  : customClassName
                    ? <>{customClassName} <span style={customBadgeStyle}>custom</span></>
                    : entry.scheduleId && !entry.scheduleClassId
                      ? <span style={warnStyle}>Schedule-only (pending class split)</span>
                      : <span style={mutedStyle}>—</span>}
              </dd>
              <dt style={dtStyle}>Source</dt>
              <dd style={ddStyle}>{classifySource || <span style={mutedStyle}>—</span>}</dd>
              <dt style={dtStyle}>Classified On</dt>
              <dd style={ddStyle}>{fmtDateTime(entry.classifiedAt)}</dd>
              <dt style={dtStyle}>Classified By</dt>
              <dd style={ddStyle}>{entry.classifiedByName || <span style={mutedStyle}>—</span>}</dd>
            </dl>
          </section>

          {/* Flags */}
          {(entry.isControlAccount || entry.excludeFromSignSweep) && (
            <section style={sectionStyle}>
              <h3 style={sectionHeaderStyle}>Flags</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {entry.isControlAccount && (
                  <span style={flagChipStyle('#f3e8ff', '#6b21a8')} title="Excluded from coverage + rollups; summed in Control Accounts widget">
                    ⚙ Control Account
                  </span>
                )}
                {entry.excludeFromSignSweep && (
                  <span style={flagChipStyle('#fde68a', '#92400e')} title="Auto cross-note sign sweep skips this ledger">
                    Excluded from Sign Sweep
                  </span>
                )}
              </div>
            </section>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

const AmtRow: React.FC<{ label: string; value: number; emphasized?: boolean; muted?: boolean }> = ({
  label, value, emphasized, muted,
}) => {
  const isDr = value > 0.005;
  const isCr = value < -0.005;
  return (
    <tr style={emphasized ? amtRowEmphasizedStyle : undefined}>
      <td style={{ ...amtLabelTdStyle, fontWeight: emphasized ? 700 : 500 }}>{label}</td>
      <td style={{
        ...amtValueTdStyle,
        fontWeight: emphasized ? 700 : 600,
        color: muted ? 'var(--text-secondary)' : isDr ? '#1e40af' : isCr ? '#991b1b' : 'var(--text-primary)',
      }}>
        {muted ? '—' : fmtAmt(value)}
      </td>
    </tr>
  );
};

const sectionStyle: React.CSSProperties = { marginBottom: 16 };
const sectionHeaderStyle: React.CSSProperties = {
  fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
  color: 'var(--text-secondary)', margin: '0 0 6px',
  borderBottom: '1px solid var(--border-color)', paddingBottom: 4,
};
const defListStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'minmax(140px, max-content) 1fr',
  rowGap: 4, columnGap: 12, margin: 0, fontSize: '0.85rem',
};
const dtStyle: React.CSSProperties = { color: 'var(--text-secondary)', fontWeight: 500 };
const ddStyle: React.CSSProperties = { margin: 0 };
const mutedStyle: React.CSSProperties = { color: 'var(--text-secondary)', fontStyle: 'italic' };
const warnStyle: React.CSSProperties = { color: '#b45309', fontWeight: 500 };
const hintStyle: React.CSSProperties = {
  fontSize: '0.72rem', color: 'var(--text-secondary)', fontStyle: 'italic',
  margin: '6px 0 0', lineHeight: 1.4,
};
const amtTableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem',
  fontVariantNumeric: 'tabular-nums',
};
const amtLabelTdStyle: React.CSSProperties = {
  padding: '5px 8px', borderBottom: '1px solid var(--border-color)',
  color: 'var(--text-secondary)',
};
const amtValueTdStyle: React.CSSProperties = {
  padding: '5px 8px', borderBottom: '1px solid var(--border-color)', textAlign: 'right',
};
const amtRowEmphasizedStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
};
const flagChipStyle = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '2px 8px', borderRadius: 4,
  background: bg, color: fg,
  fontSize: '0.75rem', fontWeight: 600,
});
const customBadgeStyle: React.CSSProperties = {
  ...flagChipStyle('#dbeafe', '#1e40af'),
  marginLeft: 6, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em',
};
const jeChipStyle: React.CSSProperties = {
  ...flagChipStyle('#fef3c7', '#92400e'),
  fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em',
};

export default FsaTrialBalanceEntryViewModal;
